// src/config/flowConfigs.ts

export type StepType = "linear" | "parallel";

export interface FlowStep {
  key: string;
  icon: string;
  type?: StepType;
  color?: string;
  note?: string;
  isRejection?: boolean;
}

export const SYSTEM_FLOWS: Record<string, FlowStep[]> = {
  Origination: [
    { key: "Application Submitted",      icon: "📋" },
    { key: "Provenir",                   icon: "⚙️", note: "Engine analysis" },
    { key: "Underwriter review",         icon: "🔍", note: "Manual verification" },
    { key: "Conditional Offer Received", icon: "📩", note: "PPI ongoing" },
    { key: "Upload customer documents",  icon: "📤" },
    { key: "Documents submitted",        icon: "📂", note: "Bank statement & DD" },
    { key: "Final Offer Received",       icon: "🏷️", note: "Doc generation" },
    { key: "Signed Documents Received",  icon: "✍️" },
    { key: "Equity Paid",                icon: "💰" },
    { key: "Underwriter",                icon: "💸", note: "Dealer payment" },
    { key: "Hydra",                      icon: "🎉" },
    // Rejection paths (not part of linear flow but detectable)
    { key: "Final offer rejected",       icon: "❌", isRejection: true },
    { key: "Offer rejected",             icon: "❌", isRejection: true },
  ],

  Hydra: [
    { key: "Pending",   icon: "⏳" },
    { key: "Approved",  icon: "✅" },
    { key: "Paid",      icon: "💰" },
    { key: "Cancelled", icon: "❌", isRejection: true },
    { key: "Disputed",  icon: "⚠️", isRejection: true },
  ],

  Fleetzen: [
    { key: "Pending",             icon: "⏳" },
    { key: "Pending AMC",         icon: "🛠️", type: "parallel" },
    { key: "Pending Tracker",     icon: "📡", type: "parallel" },
    { key: "Pending Spare Key",   icon: "🔑", type: "parallel" },
    { key: "Pending Insurance",   icon: "🛡️", type: "parallel" },
    { key: "Done",                icon: "✅" },
    { key: "Delivered",           icon: "🚚" },
  ],
};

// Alias for Origination
SYSTEM_FLOWS["Dealerplus"] = SYSTEM_FLOWS["Origination"];

/**
 * Returns the linear (happy path) steps for a system
 */
export const getLinearSteps = (system: string): FlowStep[] => {
  const all = SYSTEM_FLOWS[system] || SYSTEM_FLOWS["Origination"];
  return all.filter(s => !s.isRejection);
};

/**
 * Returns the index of the status in the system's full flow
 */
export const getActiveStepIndex = (system: string, status: string): number => {
  const steps = SYSTEM_FLOWS[system] || SYSTEM_FLOWS["Origination"];
  return steps.findIndex(s => s.key.toLowerCase() === (status || "").toLowerCase());
};

/**
 * Returns true if the status is marked as a rejection/off-track status
 */
export const isRejectionStatus = (system: string, status: string): boolean => {
  const steps = SYSTEM_FLOWS[system] || SYSTEM_FLOWS["Origination"];
  const step = steps.find(s => s.key.toLowerCase() === (status || "").toLowerCase());
  return step?.isRejection || false;
};

/**
 * Groups consecutive parallel steps into arrays
 */
export const groupParallelSteps = (steps: FlowStep[]): (FlowStep | FlowStep[])[] => {
  const result: (FlowStep | FlowStep[])[] = [];
  let currentParallel: FlowStep[] = [];

  steps.forEach((step) => {
    if (step.type === "parallel") {
      currentParallel.push(step);
    } else {
      if (currentParallel.length > 0) {
        result.push(currentParallel);
        currentParallel = [];
      }
      result.push(step);
    }
  });

  if (currentParallel.length > 0) {
    result.push(currentParallel);
  }

  return result;
};

/**
 * Returns a suitable icon for a task name
 */
export const getTaskIcon = (name: string): string => {
  const n = (name || "").toLowerCase();
  if (n.includes("insurance")) return "🛡️";
  if (n.includes("tracker")) return "📡";
  if (n.includes("key")) return "🔑";
  if (n.includes("amc")) return "🛠️";
  if (n.includes("warranty")) return "📜";
  if (n.includes("registration") || n.includes("paper")) return "📝";
  if (n.includes("delivery")) return "🚚";
  if (n.includes("pickup")) return "🛻";
  if (n.includes("license")) return "🆔";
  if (n.includes("customs")) return "🛃";
  if (n.includes("registry") || n.includes("ecmr")) return "📂";
  if (n.includes("ownership")) return "🏠";
  if (n.includes("roadworthiness")) return "✅";
  if (n.includes("credit life")) return "🧬";
  if (n.includes("lien")) return "🔗";
  return "⚙️";
};

